import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function ArticlePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://pandabuy.yachts';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "The Ultimate Pandabuy Spreadsheet Guide – Smart Shopping Strategy 2026",
            "description": "Learn how to use a pandabuy spreadsheet to compare prices, track products, and shop smarter across global platforms.",
            "url": siteUrl + "/pandabuy-spreadsheet-guide",
            "datePublished": "2026-07-01",
            "dateModified": "2026-07-01",
            "author": {
              "@type": "Organization",
              "name": "PandaBuy Yachts",
              "url": siteUrl
            },
            "publisher": {
              "@type": "Organization",
              "name": "PandaBuy Yachts",
              "url": siteUrl,
              "logo": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg"
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": siteUrl + "/pandabuy-spreadsheet-guide"
            }
          })
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full min-h-[380px] md:min-h-[460px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              'url("https://readdy.ai/api/search-image?query=Clean%20minimalist%20overhead%20photography%20of%20a%20laptop%20displaying%20organized%20spreadsheet%20data%20with%20carefully%20placed%20fashion%20accessories%20surrounding%20it%20a%20cream%20sweater%20tan%20leather%20wallet%20and%20small%20potted%20plant%20on%20light%20wooden%20desk%20soft%20natural%20morning%20light%20from%20window%20editorial%20productivity%20photography%20warm%20beige%20tones%20high%20detail&width=1600&height=900&seq=pandabuy-guide-hero-v3&orientation=landscape")',
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
        <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-6 text-center pt-20 pb-14">
          <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-300 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full mb-5">
            Complete Guide
          </span>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-white leading-tight tracking-tight">
            The Ultimate Pandabuy Spreadsheet Guide
          </h1>
          <p className="mt-4 text-sm md:text-base text-white/70 max-w-xl mx-auto leading-relaxed">
            Learn how to use a pandabuy spreadsheet to compare prices, track products, and shop smarter across global platforms.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-white/10 backdrop-blur-sm text-white text-sm font-medium rounded-full hover:bg-white/20 border border-white/20 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>
              Back to Home
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-500 text-white text-sm font-medium rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Explore Pandabuy Sheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 md:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="space-y-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
              What is a Pandabuy Spreadsheet?
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              A pandabuy spreadsheet is a structured tool that organizes product listings, prices, and specifications across multiple platforms into one easy-to-navigate format. Instead of endlessly hopping between tabs to compare sneakers, hoodies, or accessories, a pandabuy spreadsheet gives you everything in a single view — sorted, filterable, and ready for quick decision-making.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              The concept is simple: collect product data from global marketplaces, organize it by category, and add columns for price, shipping time, seller rating, and availability. The result is a pandabuy spreadsheet that transforms chaotic browsing into a streamlined, data-driven shopping experience.
            </p>

            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 pt-4">
              How to Use a Pandabuy Spreadsheet
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              Using a pandabuy spreadsheet is straightforward. Open the spreadsheet, browse through the 14 curated categories — from Shoes to Perfumes — and click into any category to see organized product listings. Each row contains essential details: product name, price range, platform source, estimated delivery time, and seller rating.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              Use the pandabuy spreadsheet filtering features to narrow results by price, rating, or shipping window. Compare similar products side by side. Note the best options, then follow the external links to make your purchase on the original platform. Throughout this process, the pandabuy spreadsheet remains your central reference point.
            </p>

            <h3 className="text-lg md:text-xl font-semibold text-foreground-900 pt-2">
              Key Benefits of a Pandabuy Spreadsheet
            </h3>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              Save time — no more opening 15 browser tabs to compare a single pair of shoes. Save money — price discrepancies across platforms become immediately visible. Make informed decisions — seller ratings and shipping estimates are right there in the spreadsheet, helping you avoid unreliable listings.
            </p>

            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 pt-4">
              The 14 Product Categories
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              The pandabuy spreadsheet covers 14 carefully selected product categories, each with its own organized section. These include Shoes, Hoodies & Sweaters, T-Shirts, Jackets, Pants & Shorts, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and a catch-all Other Stuff category. Every category features verified listings with accurate pricing.
            </p>

            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 pt-4">
              Why This Pandabuy Spreadsheet Matters in 2026
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              Cross-border shopping has never been more popular — or more complex. With new platforms, changing shipping rates, and fluctuating prices, staying organized is essential. A pandabuy spreadsheet cuts through the noise. It gives you the clarity and confidence to shop across global platforms without second-guessing your decisions.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              Whether you are a seasoned cross-border shopper or just getting started, a pandabuy spreadsheet is the smartest investment you can make in your shopping workflow. It costs nothing and saves you hours every week.
            </p>

            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 pt-4">
              Get Started Today
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              Ready to transform your shopping experience? Explore the pandabuy spreadsheet categories below. Each category connects directly to the organized spreadsheet at kaspreadsheet.com, giving you instant access to the best curated product data available.
            </p>
          </div>

          {/* CTA */}
          <div className="mt-10 p-6 md:p-8 bg-primary-50 rounded-xl border border-primary-200 text-center">
            <h3 className="text-lg font-bold text-foreground-950 mb-2">Ready to Start Shopping Smarter?</h3>
            <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
              Access the complete pandabuy spreadsheet with all 14 categories today — it is completely free.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-7 py-3 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Open Pandabuy Sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-7 py-3 bg-white border-2 border-primary-200 text-primary-700 text-sm font-semibold rounded-full hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}